import React from 'react';
import { mfaLogin } from '../../redux/auth/thunks';
import { useAppDispatch } from '../../redux/store';
import UserService from '../../services/UserService';
import validation from '@upstart/harmony/lib/validation';

/**
 * Okta login hook
 *
 * The hook will automatically dispatch the login thunk when there is a `code`
 * and `state` in the search params.
 *
 * Returns the function that redirects the user to initiate Okta authentication
 * flow.
 */
export default function useOktaLogin({
  email,
}: {
  email: string;
}): {
  redirect: () => void;
  required: boolean;
} {
  const dispatch = useAppDispatch();
  const oktaRequestRef = React.useRef<boolean>(false);
  const [required, setRequired] = React.useState<boolean>(false);

  React.useEffect(() => {
    checkOktaRequired(email)
      .then(setRequired)
      .catch(() => {
        // do nothing
      });
  }, [email]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code && state && !oktaRequestRef.current) {
      dispatch(mfaLogin({ code, state }));
      oktaRequestRef.current = true;
    }
  }, [dispatch]);

  const redirect = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    if (!BACKEND_URL) {
      throw new Error('Missing Environment variables');
    }

    const url = new URL(`${BACKEND_URL}/oidc/launchpad`);

    if (email) {
      url.searchParams.set('email', email);
    }

    window.location.href = url.href;
  };

  return { redirect, required };
}

async function checkOktaRequired(email: string | undefined): Promise<boolean> {
  if (email && validation.isEmail(email) && email.endsWith('@upstart.com')) {
    const response = await UserService.oktaRequired({ email });
    return response.data.oidcRequired;
  }

  return false;
}
